import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { effectivePricePerM2 } from "@glastotaal/core";
import { getPromotableItem, type PromotableItem } from "@/lib/catalog";
import { formatEuro } from "@/lib/format";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { priceWithSale, usePromotions } from "@/lib/promotions";
import { useRecentlyViewed } from "@/lib/recentlyViewed";
import { ProductRatingBadge } from "@/components/ProductRatingBadge";
import { useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";

/** Homepage-blok: laatst bekeken producten/glassoorten (uit localStorage), als
 *  horizontale carrousel met pijltjes (zelfde stijl als de overige pijltjes). */
export function RecentlyViewed({ excludeSlug, banded = false }: { excludeSlug?: string; banded?: boolean }) {
  const t = useT();
  const [pref] = useBtwPreference();
  const ov = useCatalogOverrides();
  const promos = usePromotions();
  const items = useRecentlyViewed()
    .filter((s) => s !== excludeSlug)
    .map(getPromotableItem)
    .filter((i): i is PromotableItem => !!i)
    .slice(0, 12);

  const ref = useRef<HTMLDivElement>(null);
  // Pijlen uitschakelen aan de randen (niet verder klikken dan er te zien is).
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = (): void => {
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [items.length]);

  if (items.length === 0) return null;

  const scroll = (dir: number): void => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  const arrow = "flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 text-primary-700 transition hover:border-primary-400 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-primary-100 disabled:text-primary-300 disabled:hover:bg-transparent";

  const section = (
    <section aria-labelledby="recent-bekeken" className="mx-auto max-w-[1600px] px-4 pt-10 sm:pt-14">
      <div className="flex items-center justify-between gap-3">
        <h2 id="recent-bekeken" className="text-lg font-bold text-primary-900 sm:text-2xl">{t("Recent bekeken")}</h2>
        <div className="flex shrink-0 gap-1.5">
          <button type="button" onClick={() => scroll(-1)} disabled={atStart} aria-label={t("Vorige")} className={arrow}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button type="button" onClick={() => scroll(1)} disabled={atEnd} aria-label={t("Volgende")} className={arrow}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </div>
      </div>

      <div ref={ref} className="mt-6 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((it) => {
          // Effectieve prijs (incl. admin-marge/-override): een op-aanvraag-artikel met
          // een ingestelde prijs toont die prijs i.p.v. "Prijs op aanvraag".
          // Actieprijs meenemen, anders staat hier een ander bedrag dan op de productpagina.
          const effCents = it.kind === "glas"
            ? it.priceCents
            : priceWithSale(effectivePricePerM2(it.priceCents, it.slug, ov, [it.category]), promos[it.slug]).priceCents;
          const onRequestEff = it.onRequest && effCents <= 0;
          return (
          <Link
            key={it.slug}
            to={it.to}
            className="group flex w-[46%] shrink-0 snap-start flex-col rounded-2xl border border-primary-100 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md sm:w-[31%] lg:w-[23%] xl:w-[15.6%]"
          >
            <div className="mb-2 aspect-square w-full overflow-hidden rounded-lg bg-white">
              {it.image ? (
                <img src={cdnImage(it.image, { w: IMG.card })} alt={t(it.name)} width={IMG.card} height={IMG.card} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-primary-200">{t("geen foto")}</div>
              )}
            </div>
            <h3 className="line-clamp-2 min-h-10 text-sm font-medium text-primary-900">{t(it.name)}</h3>
            <div className="mt-1"><ProductRatingBadge slug={it.slug} /></div>
            {/* Prijsstijl gelijk aan de rest van de site (ArticleCatalog): "vanaf" licht,
                het bedrag altijd bold in primary-900 — zo ziet elke kaart er identiek uit. */}
            <p className="mt-1 text-sm text-primary-700">
              {it.kind === "glas" && it.vanafCents != null
                ? <>{t("vanaf")} <strong className="font-bold text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong><span className="text-xs">/m² {t(btwLabel(pref))}</span></>
                : it.vanafCents != null
                  ? <>{t("vanaf")} <strong className="font-bold text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong> <span className="text-xs">{t(btwLabel(pref))}</span></>
                  : onRequestEff
                    ? <span>{t("Prijs op aanvraag")}</span>
                    : <><strong className="font-bold text-primary-900">{formatEuro(btwAmount(effCents, pref))}</strong> <span className="text-xs">{t(btwLabel(pref))}</span></>}
            </p>
          </Link>
          );
        })}
      </div>
    </section>
  );

  // Met `banded`: de sectie draagt zélf de full-width band (rand + achtergrond).
  // Zo verdwijnt de band volledig wanneer er niets bekeken is (return null hierboven),
  // i.p.v. een lege bordered strook met veel witruimte onder de contact-CTA.
  if (!banded) return section;
  return <div className="mt-12 border-t border-primary-100 bg-primary-50/40 pb-12">{section}</div>;
}
